import axios from 'axios';
import { Collection, CollectionFormData, ShareCollectionRequest, ShareCollectionResponse } from '../types/config';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export class CollectionService {
  private static readonly DEFAULT_COLLECTION_ID = 'all-documents';

  private static generateId(): string {
    return 'collection-' + Math.random().toString(36).substr(2, 9);
  }

  private static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  }

  static async getCollections(): Promise<Collection[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/collections`, this.getAuthHeaders());
      const collections = response.data.map((collection: any) => ({
        ...collection,
        createdAt: new Date(collection.createdAt),
        updatedAt: new Date(collection.updatedAt),
        shareExpiration: collection.shareExpiration ? new Date(collection.shareExpiration) : undefined,
      }));

      return collections;
    } catch (error: any) {
      console.error('Error loading collections:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        // Clear invalid token and redirect to login
        localStorage.removeItem('authToken');
        throw new Error('Authentication required. Please log in again.');
      }
      
      // For other errors, try to migrate from localStorage or create default
      return this.handleCollectionsFallback();
    }
  }

  private static async handleCollectionsFallback(): Promise<Collection[]> {
    try {
      // Try to migrate from localStorage if available
      const localStorageCollections = this.getCollectionsFromLocalStorage();
      if (localStorageCollections.length > 0) {
        console.log('Found collections in localStorage, attempting to migrate...');
        // Try to migrate these collections to backend when auth is working
        return localStorageCollections;
      }
      
      // Create a minimal default collection for fallback
      const defaultCollection: Collection = {
        id: this.DEFAULT_COLLECTION_ID,
        name: 'All Documents',
        description: 'Contains all documents',
        isDefault: true,
        documentIds: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      return [defaultCollection];
    } catch (error) {
      console.error('Fallback collection creation failed:', error);
      return [];
    }
  }

  private static getCollectionsFromLocalStorage(): Collection[] {
    try {
      const stored = localStorage.getItem('collections');
      if (stored) {
        const collections = JSON.parse(stored);
        return collections.map((c: any) => ({
          ...c,
          createdAt: c.createdAt ? new Date(c.createdAt) : new Date(),
          updatedAt: c.updatedAt ? new Date(c.updatedAt) : new Date(),
          shareExpiration: c.shareExpiration ? new Date(c.shareExpiration) : undefined,
        }));
      }
    } catch (error) {
      console.error('Error reading collections from localStorage:', error);
    }
    return [];
  }

  private static async createDefaultCollection(): Promise<Collection> {
    const defaultCollection = {
      id: this.DEFAULT_COLLECTION_ID,
      name: 'All Documents',
      description: 'Contains all documents',
      isDefault: true,
      documentIds: [] as string[],
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/api/collections`, defaultCollection, this.getAuthHeaders());
      return {
        ...response.data,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
      };
    } catch (error) {
      console.error('Error creating default collection:', error);
      throw error;
    }
  }

  static async getCollectionById(id: string): Promise<Collection | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/collections/${id}`, this.getAuthHeaders());
      return {
        ...response.data,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
        shareExpiration: response.data.shareExpiration ? new Date(response.data.shareExpiration) : undefined,
      };
    } catch (error) {
      console.error('Error getting collection by ID:', error);
      return null;
    }
  }

  static async getDefaultCollection(): Promise<Collection | null> {
    const collections = await this.getCollections();
    return collections.find(c => c.isDefault) || collections[0] || null;
  }

  static async createCollection(data: CollectionFormData): Promise<Collection> {
    try {
      const newCollection = {
        id: this.generateId(),
        name: data.name,
        description: data.description,
        isDefault: data.isDefault,
        documentIds: [...data.documentIds],
      };

      const response = await axios.post(`${API_BASE_URL}/api/collections`, newCollection, this.getAuthHeaders());
      return {
        ...response.data,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
      };
    } catch (error) {
      console.error('Error creating collection:', error);
      throw new Error('Failed to create collection');
    }
  }

  static async updateCollection(id: string, data: Partial<CollectionFormData>): Promise<Collection> {
    try {
      // Get the current collection to merge with partial data
      const existingCollection = await this.getCollectionById(id);
      if (!existingCollection) {
        throw new Error('Collection not found');
      }
      
      // Merge existing data with updates to ensure required fields are present
      const updateData = {
        id: existingCollection.id,
        name: data.name !== undefined ? data.name : existingCollection.name,
        description: data.description !== undefined ? data.description : existingCollection.description,
        isDefault: data.isDefault !== undefined ? data.isDefault : existingCollection.isDefault,
        documentIds: data.documentIds !== undefined ? data.documentIds : existingCollection.documentIds,
      };
      
      const response = await axios.put(`${API_BASE_URL}/api/collections/${id}`, updateData, this.getAuthHeaders());
      return {
        ...response.data,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
        shareExpiration: response.data.shareExpiration ? new Date(response.data.shareExpiration) : undefined,
      };
    } catch (error) {
      console.error('Error updating collection:', error);
      throw new Error('Failed to update collection');
    }
  }

  static async deleteCollection(id: string): Promise<void> {
    if (id === this.DEFAULT_COLLECTION_ID) {
      throw new Error('Cannot delete the default "All Documents" collection');
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/collections/${id}`, this.getAuthHeaders());
    } catch (error) {
      console.error('Error deleting collection:', error);
      throw new Error('Failed to delete collection');
    }
  }

  static async addDocumentToCollection(collectionId: string, documentId: string): Promise<void> {
    const collection = await this.getCollectionById(collectionId);
    
    if (!collection) {
      throw new Error('Collection not found');
    }

    if (!collection.documentIds.includes(documentId)) {
      collection.documentIds.push(documentId);
      await this.updateCollection(collectionId, { documentIds: collection.documentIds });
    }
  }

  static async removeDocumentFromCollection(collectionId: string, documentId: string): Promise<void> {
    if (collectionId === this.DEFAULT_COLLECTION_ID) {
      return; // Cannot remove documents from "All Documents" collection
    }

    const collection = await this.getCollectionById(collectionId);
    
    if (!collection) {
      throw new Error('Collection not found');
    }

    collection.documentIds = collection.documentIds.filter(id => id !== documentId);
    await this.updateCollection(collectionId, { documentIds: collection.documentIds });
  }

  static async getDocumentsByCollection(collectionId: string): Promise<string[]> {
    const collection = await this.getCollectionById(collectionId);
    if (!collection) {
      return [];
    }
    return [...collection.documentIds];
  }

  static async getCollectionsForDocument(documentId: string): Promise<Collection[]> {
    const collections = await this.getCollections();
    return collections.filter(c => 
      c.id === this.DEFAULT_COLLECTION_ID || c.documentIds.includes(documentId)
    );
  }

  static async syncWithDocuments(allDocumentIds: string[]): Promise<void> {
    const collections = await this.getCollections();
    
    // Update "All Documents" collection to include all document IDs in the same order
    const allDocsCollection = collections.find(c => c.id === this.DEFAULT_COLLECTION_ID);
    if (allDocsCollection) {
      await this.updateCollection(allDocsCollection.id, { documentIds: [...allDocumentIds] });
    }

    // Remove deleted documents from other collections while preserving order
    for (const collection of collections) {
      if (collection.id !== this.DEFAULT_COLLECTION_ID) {
        const originalLength = collection.documentIds.length;
        const filteredIds = collection.documentIds.filter(id => allDocumentIds.includes(id));
        if (filteredIds.length !== originalLength) {
          await this.updateCollection(collection.id, { documentIds: filteredIds });
        }
      }
    }
  }

  static async reorderDocumentsInCollection(collectionId: string, fromIndex: number, toIndex: number): Promise<void> {
    if (fromIndex === toIndex) return;

    const collection = await this.getCollectionById(collectionId);
    
    if (!collection) {
      throw new Error('Collection not found');
    }

    // Reorder documents within the collection
    const newDocumentIds = [...collection.documentIds];
    const [movedDocumentId] = newDocumentIds.splice(fromIndex, 1);
    newDocumentIds.splice(toIndex, 0, movedDocumentId);

    await this.updateCollection(collectionId, { documentIds: newDocumentIds });
  }

  static async cloneCollection(sourceCollectionId: string, newName: string, newDescription?: string): Promise<Collection> {
    const collections = await this.getCollections();
    const sourceCollection = collections.find(c => c.id === sourceCollectionId);
    
    if (!sourceCollection) {
      throw new Error('Source collection not found');
    }

    if (sourceCollection.id === this.DEFAULT_COLLECTION_ID) {
      throw new Error('Cannot clone the "All Documents" collection');
    }

    // Check if name already exists
    if (collections.some(c => c.name.toLowerCase() === newName.toLowerCase())) {
      throw new Error('A collection with this name already exists');
    }

    const clonedCollectionData: CollectionFormData = {
      name: newName,
      description: newDescription || `Copy of ${sourceCollection.name}`,
      isDefault: false, // Cloned collections are never default
      documentIds: [...sourceCollection.documentIds], // Copy the document IDs
    };

    return await this.createCollection(clonedCollectionData);
  }

  // Migration helper to move localStorage collections to backend
  static async migrateLocalStorageCollections(): Promise<void> {
    try {
      const localCollections = this.getCollectionsFromLocalStorage();
      if (localCollections.length === 0) {
        return; // Nothing to migrate
      }

      console.log(`Migrating ${localCollections.length} collections from localStorage to backend...`);
      
      for (const collection of localCollections) {
        if (collection.id !== this.DEFAULT_COLLECTION_ID) {
          try {
            const formData: CollectionFormData = {
              name: collection.name,
              description: collection.description,
              isDefault: collection.isDefault,
              documentIds: collection.documentIds,
            };
            await this.createCollection(formData);
            console.log(`Migrated collection: ${collection.name}`);
          } catch (error) {
            console.warn(`Failed to migrate collection ${collection.name}:`, error);
          }
        }
      }
      
      // Clear localStorage after successful migration
      localStorage.removeItem('collections');
      console.log('Successfully migrated collections and cleared localStorage');
    } catch (error) {
      console.error('Error during migration:', error);
    }
  }

  // Collection sharing methods
  static async generateShareLink(collectionId: string, request: ShareCollectionRequest = {}): Promise<ShareCollectionResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/collections/${collectionId}/share`, request, this.getAuthHeaders());
      return {
        ...response.data,
        expiresAt: new Date(response.data.expiresAt),
        collection: {
          ...response.data.collection,
          createdAt: new Date(response.data.collection.createdAt),
          updatedAt: new Date(response.data.collection.updatedAt),
          shareExpiration: response.data.collection.shareExpiration ? new Date(response.data.collection.shareExpiration) : undefined,
        }
      };
    } catch (error: any) {
      console.error('Failed to generate share link:', error);
      throw new Error(error.response?.data?.message || 'Failed to generate share link');
    }
  }

  static async removeShareLink(collectionId: string): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/api/collections/${collectionId}/share`, this.getAuthHeaders());
    } catch (error: any) {
      console.error('Failed to remove share link:', error);
      throw new Error(error.response?.data?.message || 'Failed to remove share link');
    }
  }

  static async getSharedCollection(shareId: string): Promise<Collection | null> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/collections/shared/${shareId}`);
      return {
        ...response.data,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
        shareExpiration: response.data.shareExpiration ? new Date(response.data.shareExpiration) : undefined,
      };
    } catch (error) {
      console.error('Error getting shared collection:', error);
      return null;
    }
  }

  static async validateShareLink(shareId: string): Promise<{ valid: boolean; collection?: Collection; reason?: string }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/collections/shared/${shareId}/validate`);
      if (response.data.valid && response.data.collection) {
        return {
          valid: true,
          collection: {
            ...response.data.collection,
            createdAt: new Date(response.data.collection.createdAt),
            updatedAt: new Date(response.data.collection.updatedAt),
            shareExpiration: response.data.collection.shareExpiration ? new Date(response.data.collection.shareExpiration) : undefined,
          }
        };
      }
      return response.data;
    } catch (error) {
      console.error('Error validating share link:', error);
      return { valid: false, reason: 'Share link validation failed' };
    }
  }
}